// Copyright Epic Games, Inc. All Rights Reserved.

import React, { useRef, useState } from "react";
import {
  Box,
  Grid,
  IconButton,
  styled,
  SvgIcon,
  SvgIconProps,
  Typography,
} from "@material-ui/core";
import { EpicIcon } from "./EpicIcon";
import { Plus, Minus } from "@epic-ui/icons";
import { useCancelInstallationDispatchAction } from "../../store/installation/hooks";

function Square(props: SvgIconProps) {
  return (
    <SvgIcon {...props}>
      <path d="M2 2h20L22 0H2z" />
      <path d="M0 24h2L2 0H0z" />
      <path d="M22 24h2L24 0H22z" />
      <path d="M2 24h20L22 22H2z" />
    </SvgIcon>
  );
}

const Header = styled(Grid)(({ theme }) => ({
  padding: "7px",
  height: "29px",
  borderBottom: `1px solid ${theme.palette.grey.A400}`,
}));

const MinusIcon = styled(Minus)({ width: 10, height: 10 });
const SquareIcon = styled(Square)({ width: 10, height: 10 });
const PlusIcon = styled(Plus)({
  width: 12,
  height: 12,
  transform: "rotate(45deg)",
});
const AppTitle = styled(Typography)({
  paddingLeft: "7px",
  fontSize: "10px",
});

export function AppBar(): JSX.Element {
  const refDrag = useRef(false);
  const [maximized, setMaximized] = useState(false);
  const cancelInstallation = useCancelInstallationDispatchAction();

  const handleMinMax = async () => {
    try {
      if (maximized) {
        // eslint-disable-next-line  @typescript-eslint/no-unsafe-call
        const result = await window.ue.ui.restoreWindow();

        setMaximized(!result);
      } else {
        // eslint-disable-next-line  @typescript-eslint/no-unsafe-call
        const result = await window.ue.ui.maximizeWindow();

        setMaximized(result);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleClose = () => {
    cancelInstallation();
    window.close();
  };

  const handleHide = () => {
    // eslint-disable-next-line  @typescript-eslint/no-unsafe-call
    window.ue.ui.hideWindow();
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    // eslint-disable-next-line  @typescript-eslint/no-unsafe-call
    window.ue.ui.startWindowDrag(
      e.clientX * window.devicePixelRatio,
      e.clientY * window.devicePixelRatio
    );
    refDrag.current = true;
  };

  const handleMouseUp = () => {
    if (refDrag.current) {
      // eslint-disable-next-line  @typescript-eslint/no-unsafe-call
      window.ue.ui.stopWindowDrag();
      refDrag.current = false;
    }
  };

  const handleMousedownForButtons = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  // We fallback to default text only for dev purposes in real world that should never happen.
  const title: string = (window.ue && window.ue.ui.title) || "UI Helper";
  const isWindowResizable: boolean =
    (window.ue && window.ue.ui.isResizable) || false;

  return (
    <Header
      container
      direction="row"
      justify="space-between"
      alignContent="center"
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
    >
      <Box display="flex" justifyContent="left" alignItems="center">
        <EpicIcon size="1rem" marginBottom="0px" />
        <AppTitle variant="body1">{title}</AppTitle>
      </Box>

      <Box
        display="flex"
        justifyContent="end"
        alignItems="center"
        width="100px"
        onMouseDown={handleMousedownForButtons}
      >
        <IconButton onClick={handleHide}>
          <MinusIcon />
        </IconButton>

        <IconButton disabled={!isWindowResizable} onClick={handleMinMax}>
          <SquareIcon />
        </IconButton>

        <IconButton onClick={handleClose}>
          <PlusIcon />
        </IconButton>
      </Box>
    </Header>
  );
}
